import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import { TurnstileController } from "~/lib/antibot/turnstile-controller";

import { VerificationNotice } from "./VerificationNotice";

import type { TurnstileProof, TurnstileSnapshot } from "~/lib/antibot/turnstile-controller";
import type { AuthSurface } from "~/lib/antibot/types";

/**
 * Cloudflare Turnstile (anti-bot) para los formularios que piden un código por WhatsApp, en dos
 * niveles: el invisible (siempre) y el que puede pedir un clic (sólo si hace falta). La lógica
 * vive en `TurnstileController`; acá están el hook y el lugar del formulario donde se dibuja.
 *
 * Uso: `const turnstile = useTurnstile({ surface: "login" })`, `<TurnstileSlot turnstile={…} />`
 * adentro del formulario, y al enviar `await turnstile.getProof()` en vez de mandar el token que
 * haya en ese instante. El widget entrega el token ASINCRÓNICAMENTE, un segundo después de
 * montarse: un formulario que manda lo que tiene en el momento rebota a todo el que completa
 * rápido (autocompletar), con un error que no se ganó. Los formularios en general no usan esto
 * directo: usan `useAuthGuard`, que además reintenta solo cuando la API pide el clic.
 *
 * Sin config del antibot (apagado en la API y sin `VITE_TURNSTILE_SITE_KEY`) no se monta nada y
 * `getProof()` vuelve enseguida sin token: los formularios se comportan como siempre.
 */

export type { TurnstileProof } from "~/lib/antibot/turnstile-controller";

export type UseTurnstileResult = {
  controller: TurnstileController;
  snapshot: TurnstileSnapshot;
  /** Hay un `getProof()` esperando un token: el botón dice "Verificando…". */
  verifying: boolean;
  /** La constancia para el pedido: espera, escala si hace falta, nunca cuelga. */
  getProof: () => Promise<TurnstileProof>;
  /** Alias de antes: sólo el token (undefined si no hubo). */
  awaitToken: () => Promise<string | undefined>;
  /** Intento nuevo (otro `attemptId`) y token fresco: los tokens son de un solo uso. */
  reset: () => void;
  /** Token fresco para el mismo intento (la API dijo que el anterior venció). */
  refresh: () => void;
  /** Pasar al nivel con clic (p. ej. porque la API lo pidió). */
  escalate: (reason: string, managedSiteKey?: string | null) => void;
  /** El id del intento en curso (el `cData` del widget, el `attemptId` del pedido). */
  getAttemptId: () => string;
};

export function useTurnstile({ surface }: { surface: AuthSurface }): UseTurnstileResult {
  const [controller] = useState(() => new TurnstileController(surface));

  useEffect(() => {
    controller.activate();
    return () => controller.deactivate();
  }, [controller]);

  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getServerSnapshot,
  );

  // Memoizado para que los formularios lo puedan poner en dependencias sin rearmar sus handlers
  // en cada tecla.
  return useMemo(
    () => ({
      controller,
      snapshot,
      verifying: snapshot.verifying,
      getProof: controller.getProof,
      awaitToken: async () => (await controller.getProof()).token,
      reset: controller.reset,
      refresh: controller.refresh,
      escalate: controller.escalate,
      getAttemptId: controller.getAttemptId,
    }),
    [controller, snapshot],
  );
}

/** Copia para cuando el nivel con clic no pudo completar, según el motivo. */
export function managedFailureCopy(reason: string): { title: string; body: string } {
  const title = "No pudimos completar la comprobación";
  if (reason === "unsupported") {
    return {
      title,
      body: "Tu navegador no puede hacer la comprobación de seguridad. Probá actualizarlo o usá otro.",
    };
  }
  if (reason === "script_error" || reason === "script_timeout" || reason === "render_error") {
    return {
      title,
      body: "No pudo cargar la comprobación de seguridad. Suele ser la conexión o un bloqueador de anuncios.",
    };
  }
  // 200100: reloj o caché del dispositivo (Cloudflare no puede validar un desafío "del futuro").
  if (reason.startsWith("200100")) {
    return {
      title,
      body: "Revisá que la fecha y la hora de tu teléfono estén bien, y probá de nuevo.",
    };
  }
  return {
    title,
    body: "Suele ser la conexión o una extensión del navegador que la bloquea. Probá de nuevo.",
  };
}

type TurnstileSlotProps = {
  turnstile: UseTurnstileResult;
  /**
   * Qué hace "Reintentar" cuando el nivel con clic no pudo completar: el mismo envío del
   * formulario (que vuelve a pedir la comprobación y, al tener el token, sigue solo).
   */
  onRetry?: () => void;
};

/**
 * El lugar del formulario donde vive el antibot. En el caso normal no ocupa lugar: el contenedor
 * del widget invisible queda fuera del flujo (el widget no se ve) y no suma un renglón vacío al
 * formulario. Si hace falta el nivel con clic, aparece nuestro texto corto y la casilla de
 * Cloudflare; si eso tampoco funciona, un mensaje claro con "Reintentar".
 *
 * Devuelve hermanos, no un contenedor, para que el formulario los espacie como al resto de sus
 * filas. Uno por formulario a la vez (el paso del teléfono o el del código, nunca los dos).
 */
export function TurnstileSlot({ turnstile, onRetry }: TurnstileSlotProps) {
  const { controller, snapshot } = turnstile;
  const promptRef = useRef<HTMLDivElement>(null);

  // En el celular la casilla puede quedar debajo del teclado o fuera de la pantalla: se la trae.
  useEffect(() => {
    if (snapshot.interaction !== "prompt") return;
    promptRef.current?.scrollIntoView?.({ block: "center", behavior: "smooth" });
  }, [snapshot.interaction]);

  const failure = snapshot.failure ? managedFailureCopy(snapshot.failure) : null;

  return (
    <>
      <div ref={controller.attachInvisible} aria-hidden="true" className="absolute" />

      {snapshot.tier === "managed" && (
        <div className="grid gap-2">
          {snapshot.interaction && (
            <div
              ref={promptRef}
              role="status"
              // polite: explica lo que aparece abajo, no interrumpe.
              aria-live="polite"
              className="grid gap-0.5 text-center"
            >
              {snapshot.interaction === "prompt" ? (
                <>
                  <p className="font-display text-[14.5px] font-bold leading-snug text-landing-text">
                    Confirmá que sos una persona
                  </p>
                  <p className="text-[13px] leading-relaxed text-landing-muted">
                    Es un toque, para frenar a los robots.
                  </p>
                </>
              ) : (
                <p className="text-[13px] leading-relaxed text-landing-muted">Listo, seguimos…</p>
              )}
            </div>
          )}
          {/* Nunca `display: none`: el ancho se mide al montar la casilla para elegir el
              tamaño que entra. Vacío no ocupa lugar. */}
          <div ref={controller.attachManaged} className="flex w-full justify-center" />
        </div>
      )}

      {failure && (
        <VerificationNotice
          title={failure.title}
          body={failure.body}
          className="text-left"
          action={
            onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className="justify-self-start rounded-[6px] border border-landing-gate/40 bg-landing-white px-3 py-1.5 text-[13px] font-semibold text-landing-text transition-colors hover:bg-landing-gate-bg"
              >
                Reintentar
              </button>
            ) : undefined
          }
        />
      )}
    </>
  );
}
